import React, { useEffect, useState } from "react";
import { useParams, useNavigate, useLocation } from "react-router-dom";
import {
  Button,
  message,
  Card,
  Input,
  Select,
  Typography,
  Badge,
  Tag,
  InputNumber,
  Form,
} from "antd";
import { PlusCircleFilled } from "@ant-design/icons";
import DashboardLayout from "examples/LayoutContainers/DashboardLayout";
import DashboardNavbar from "examples/Navbars/DashboardNavbar";
import { useCareerPath } from "Hooks/AdminHooks/Organization/useCareerPath";
import { useDepartmentDetail } from "Hooks/AdminHooks/Organization/useDepartmentDetail";

const { TextArea } = Input;
const { Text } = Typography;

export const CreateJobTile = () => {
  // 1. FIX: Changed variable name to [formInstance] to avoid conflict with the <Form> component
  const [formInstance] = Form.useForm();
  const { careerPathId, jobTitleId } = useParams();
  const location = useLocation();
  const departmentId = location.state?.departmentId;
  const careerPathIdFromState = location.state?.careerPathId;
  const navigate = useNavigate();
  const isEdit = Boolean(jobTitleId);

  const { createJobTitle, careerPath, fetchCareerPath, fetchSkills, skills, loadingSkills } =
    useCareerPath();
  const { fetchJobTitle, jobTitle, updateJobTitle } = useDepartmentDetail();

  const jobTitles = careerPath?.jobTitles || [];
  const sortedTitles = [...jobTitles].sort((a, b) => a.stepOrder - b.stepOrder);

  const lastJob = sortedTitles.length ? sortedTitles[sortedTitles.length - 1] : null;
  const minAllowed = lastJob ? lastJob.maxExperienceRequired ?? lastJob.minExperienceRequired : 0;
  const nextStepOrder = lastJob ? lastJob.stepOrder + 1 : 1;

  const [customSkillInput, setCustomSkillInput] = useState("");

  // Keep only the dynamic lists in state; the rest is handled by formInstance
  const [skillLists, setSkillLists] = useState({
    skillIds: [],
    customSkillNames: [],
  });

  // Fetch initial data
  useEffect(() => {
    if (departmentId) fetchSkills(departmentId);
    const effectiveCareerPathId = careerPathId || careerPathIdFromState;
    if (effectiveCareerPathId) fetchCareerPath(effectiveCareerPathId);
    if (jobTitleId) fetchJobTitle(jobTitleId);
  }, [careerPathId, careerPathIdFromState, departmentId, jobTitleId]);

  // Pre-fill the form once the existing job title has loaded (edit mode)
  useEffect(() => {
    if (isEdit && jobTitle) {
      formInstance.setFieldsValue({
        name: jobTitle.name,
        description: jobTitle.description,
        minExperienceRequired: jobTitle.minExperienceRequired,
      });
      setSkillLists({
        skillIds: (jobTitle.skills || []).map((s) => s.id),
        customSkillNames: [],
      });
    }
  }, [isEdit, jobTitle, formInstance]);

  // 2. FIX: Synchronize formInstance values when minAllowed changes (create mode only)
  useEffect(() => {
    if (isEdit) return;
    formInstance.setFieldsValue({
      minExperienceRequired: minAllowed,
    });
  }, [minAllowed, formInstance, isEdit]);

  // Skill Management
  const handleSelectSkill = (id) => {
    if (id && !skillLists.skillIds.includes(id)) {
      setSkillLists((prev) => ({ ...prev, skillIds: [...prev.skillIds, id] }));
    }
  };

  const addCustomSkill = () => {
    const skill = customSkillInput.trim();
    if (skill && !skillLists.customSkillNames.includes(skill)) {
      setSkillLists((prev) => ({ ...prev, customSkillNames: [...prev.customSkillNames, skill] }));
    }
    setCustomSkillInput("");
  };

  const removeSkill = (id) =>
    setSkillLists((prev) => ({ ...prev, skillIds: prev.skillIds.filter((sid) => sid !== id) }));
  const removeCustomSkill = (name) =>
    setSkillLists((prev) => ({
      ...prev,
      customSkillNames: prev.customSkillNames.filter((s) => s !== name),
    }));

  const handleSubmit = async (values) => {
    try {
      let result;
      if (isEdit) {
        const payload = {
          id: parseInt(jobTitleId),
          ...values,
          skillIds: skillLists.skillIds,
          customSkillNames: skillLists.customSkillNames,
        };
        result = await updateJobTitle(payload);
        if (result) {
          message.success("Job Title updated successfully");
        }
      } else {
        const payload = {
          ...values,
          careerPathId: parseInt(careerPathId),
          stepOrder: nextStepOrder, // Hidden background value
          skillIds: skillLists.skillIds,
          customSkillNames: skillLists.customSkillNames,
        };
        result = await createJobTitle(payload);
        if (result) {
          message.success("Job Title added successfully");
          fetchCareerPath(careerPathId);
          formInstance.resetFields();
          setSkillLists({ skillIds: [], customSkillNames: [] });
        }
      }
      if (result) {
        setTimeout(() => {
          navigate(-1);
        }, 2000);
      }
    } catch (error) {
      console.error("Submission failed:", error);
    }
  };

  return (
    <DashboardLayout>
      <DashboardNavbar />
      <div style={{ padding: "24px" }}>
        <h2 className="fw-bold mb-4">Setup Career Path: {careerPath?.name || "..."}</h2>

        <div className="row">
          {/* LEFT COLUMN: Visuals (Keeping your existing logic) */}
          <div className="col-md-5">
            <Card title="Career Sequence" className="shadow-sm">
              <div
                style={{ display: "flex", flexDirection: "column-reverse", alignItems: "center" }}
              >
                {sortedTitles.map((job, index) => (
                  <React.Fragment key={job.id}>
                    {index !== 0 && (
                      <div style={{ width: "2px", height: "15px", backgroundColor: "#e8e8e8" }} />
                    )}
                    <div
                      style={{
                        width: "100%",
                        maxWidth: "300px",
                        padding: "15px",
                        borderRadius: "8px",
                        border: "1px solid #f0f0f0",
                        backgroundColor: "#fff",
                      }}
                    >
                      <div className="d-flex justify-content-between align-items-center">
                        <Text strong>{job.name}</Text>
                        <Badge
                          count={job.stepOrder}
                          style={{ backgroundColor: "#f5f5f5", color: "#595959" }}
                        />
                      </div>
                      <Text type="secondary" style={{ fontSize: "12px" }}>
                        Experience: {job.minExperienceRequired}{" "}
                        {job.maxExperienceRequired ? `- ${job.maxExperienceRequired}` : "+"} Yrs
                      </Text>
                    </div>
                  </React.Fragment>
                ))}
                {sortedTitles.length > 0 && (
                  <div style={{ width: "2px", height: "20px", backgroundColor: "#e8e8e8" }} />
                )}
                <PlusCircleFilled
                  style={{ fontSize: "30px", color: "#52c41a", marginBottom: "10px" }}
                />
                <div
                  style={{
                    width: "100%",
                    maxWidth: "300px",
                    padding: "15px",
                    border: "2px dashed #52c41a",
                    borderRadius: "8px",
                    textAlign: "center",
                    backgroundColor: "#f6ffed",
                  }}
                >
                  <Text strong style={{ color: "#389e0d" }}>
                    Next: Step {nextStepOrder}
                  </Text>
                  <br />
                  <Text type="secondary" style={{ fontSize: "12px" }}>
                    Min Experience: {minAllowed}+ years
                  </Text>
                </div>
              </div>
            </Card>
          </div>

          {/* RIGHT COLUMN: Professional Form */}
          <div className="col-md-7">
            <Card title="Add Job Title to Path" className="shadow-sm">
              <Form form={formInstance} layout="vertical" onFinish={handleSubmit}>
                <Form.Item
                  name="name"
                  label={<span className="fw-bold">Job Title Name</span>}
                  rules={[{ required: true, message: "Required" }]}
                >
                  <Input size="large" placeholder="e.g. Senior Frontend Engineer" />
                </Form.Item>

                <div className="row">
                  <Form.Item
                    name="description"
                    label={<span className="fw-bold">Description</span>}
                  >
                    <TextArea rows={3} placeholder="Describe role responsibilities..." />
                  </Form.Item>
                  <div className="col-md-12">
                    <Form.Item
                      name="minExperienceRequired"
                      label={<span className="fw-bold">Min. Years Experience</span>}
                      extra={
                        <Text type="secondary" style={{ fontSize: "12px" }}>
                          Must be at least {minAllowed} based on previous role.
                        </Text>
                      }
                      rules={[
                        { required: true, message: "Required" },
                        {
                          validator: (_, value) =>
                            value < minAllowed
                              ? Promise.reject(`Minimum allowed is ${minAllowed}`)
                              : Promise.resolve(),
                        },
                      ]}
                    >
                      <InputNumber size="large" style={{ width: "100%" }} step={0.1} />
                    </Form.Item>
                  </div>
                </div>

                <hr />
                <h6 className="fw-bold mb-3">Required Skills</h6>
                <div className="row">
                  <div className="col-md-6 mb-3">
                    <label className="form-label small">Select Skills</label>
                    <Select
                      showSearch
                      placeholder="Select a skill"
                      style={{ width: "100%" }}
                      onChange={handleSelectSkill}
                      value=""
                      loading={loadingSkills}
                      optionFilterProp="children"
                    >
                      {skills.map((skill) => (
                        <Select.Option key={skill.id} value={skill.id}>
                          {skill.name}
                        </Select.Option>
                      ))}
                    </Select>
                  </div>

                  <div className="col-md-6 mb-3">
                    <label className="form-label small">Add Custom Skill</label>
                    <div className="d-flex gap-2">
                      <Input
                        placeholder="Type and press Enter"
                        value={customSkillInput}
                        onChange={(e) => setCustomSkillInput(e.target.value)}
                        onPressEnter={(e) => {
                          e.preventDefault();
                          addCustomSkill();
                        }}
                      />
                      <Button onClick={addCustomSkill}>Add</Button>
                    </div>
                  </div>
                </div>

                <div
                  className="mb-4 p-2"
                  style={{ minHeight: "40px", background: "#fbfbfb", borderRadius: "4px" }}
                >
                  {skillLists.skillIds.map((id) => (
                    <Tag
                      key={id}
                      closable
                      color="gold"
                      className="m-1"
                      onClose={() => removeSkill(id)}
                    >
                      {skills.find((s) => s.id === id)?.name}
                    </Tag>
                  ))}
                  {skillLists.customSkillNames.map((name) => (
                    <Tag
                      key={name}
                      closable
                      color="green"
                      className="m-1"
                      onClose={() => removeCustomSkill(name)}
                    >
                      {name}
                    </Tag>
                  ))}
                </div>

                <div className="d-flex justify-content-end gap-2 mt-4">
                  <Button size="large" onClick={() => navigate(-1)}>
                    Cancel
                  </Button>
                  <Button size="large" type="primary" htmlType="submit">
                    Save Job Title
                  </Button>
                </div>
              </Form>
            </Card>
          </div>
        </div>
      </div>
    </DashboardLayout>
  );
};

export default CreateJobTile;
